import { useDisclosure, Card, Icon, Portal } from '@chakra-ui/react';
import { IAccountingPeriodLock } from '@monetize/types/app';
import { format as formatDate } from 'date-fns/format';
import { useMemo, useRef } from 'react';
import { MdClose, MdOutlineHistory } from 'react-icons/md';
import { useGetAccountingPeriodLockHistory } from '../../api/revenueService';
import { useUserDisplayNames } from '../../api/usersService';
import {
  MBox,
  MButton,
  MCard,
  MCircularProgress,
  MCustomIconButton,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
  MStack,
  MText,
  MTooltip,
} from '../../components/Monetize';
import MEmptyDataPlaceholder from '../../components/Monetize/MEmptyDataPlaceholder';
import { DateDisplay } from '../../components/Monetize/DateDisplay';
import { useAuth } from '../../services/auth0';

function formatPeriod(year: number, month: number): string {
  return formatDate(new Date(year, month - 1, 1), 'MMMM yyyy');
}

const LockHistoryItem = ({
  lock,
  lockedByDisplay,
}: {
  lock: IAccountingPeriodLock;
  lockedByDisplay: string;
}) => {
  const lockedDate = lock.persistenceMetadata?.createDate;

  return (
    <MCard variant="borderless">
      <Card.Body flex="1" flexDirection="column">
        <MText fontSize="md" fontWeight="bold" color="tPurple.base">
          {formatPeriod(lock.year, lock.month)}
        </MText>
        <MFlex mt={1} gap={1} alignItems="center">
          <MText fontSize="sm" color="tGray.darkPurple">
            Locked by:
          </MText>
          <MText fontSize="sm" fontWeight="medium" color="tPurple.base">
            {lockedByDisplay}
          </MText>
        </MFlex>
        {lockedDate && (
          <DateDisplay
            date={lockedDate}
            timezoneConfigKey="userTimezone"
            textProps={{
              fontSize: 'sm',
              fontWeight: 'normal',
              color: 'tGray.darkPurple',
              mt: 1,
            }}
          />
        )}
      </Card.Body>
    </MCard>
  );
};

export const AccountingPeriodLockHistoryDrawer = () => {
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef<HTMLButtonElement>(null);
  const { tenantId } = useAuth();

  const { data: locks, isLoading } = useGetAccountingPeriodLockHistory({
    enabled: isOpen,
    meta: { showErrorToast: true },
  });

  const uniqueUserIds = useMemo(
    () => [
      ...new Set(
        (locks ?? [])
          .map((lock) => lock.lockedBy ?? lock.persistenceMetadata?.createdBy)
          .filter((id): id is string => !!id),
      ),
    ],
    [locks],
  );

  const { userNameMap, isLoading: isLoadingUsers } = useUserDisplayNames(
    tenantId,
    uniqueUserIds,
  );

  const hasLocks = (locks?.length ?? 0) > 0;

  return (
    <MBox>
      <MTooltip label="View lock history" placement="bottom-end">
        <MCustomIconButton
          ref={btnRef}
          icon={MdOutlineHistory}
          aria-label="View accounting period lock history"
          onClick={onOpen}
        />
      </MTooltip>
      <MDrawer
        open={isOpen}
        placement="end"
        onOpenChange={(e) => {
          if (!e.open) {
            onClose();
          }
        }}
        size="sm"
        finalFocusEl={() => btnRef.current}
        preventScroll={false}
      >
        <Portal>
          <MDrawerOverlay />
          <MDrawerPositioner>
            <MDrawerContent>
              <MDrawerCloseButton>
                <Icon asChild>
                  <MdClose />
                </Icon>
              </MDrawerCloseButton>
              <MDrawerHeader>Accounting Period Lock History</MDrawerHeader>
              <MDrawerBody px={2} bgColor="tGray.sidebarDark">
                {(isLoading || isLoadingUsers) && (
                  <MFlex w="full" h="full" justify="center" align="center">
                    <MCircularProgress isIndeterminate />
                  </MFlex>
                )}
                {!isLoading && !isLoadingUsers && !hasLocks && (
                  <MFlex flexDirection="column" bgColor="tWhite.base">
                    <MEmptyDataPlaceholder
                      mainMessage="No lock history found."
                      boxProps={{ h: '3.25rem' }}
                      mainMessageProps={{
                        fontSize: 'md',
                        fontWeight: 'semibold',
                      }}
                    />
                  </MFlex>
                )}
                {!isLoading && !isLoadingUsers && hasLocks && (
                  <MStack gap={2}>
                    {locks!.map((lock) => {
                      const userId =
                        lock.lockedBy ?? lock.persistenceMetadata?.createdBy;
                      const lockedByDisplay = userId
                        ? (userNameMap[userId] ?? userId)
                        : '—';
                      return (
                        <LockHistoryItem
                          key={lock.id}
                          lock={lock}
                          lockedByDisplay={lockedByDisplay}
                        />
                      );
                    })}
                  </MStack>
                )}
              </MDrawerBody>
              <MDrawerFooter>
                <MFlex justify="center" w="full">
                  <MButton
                    mt={5}
                    variant="cancel"
                    justifySelf="center"
                    onClick={onClose}
                  >
                    Close
                  </MButton>
                </MFlex>
              </MDrawerFooter>
            </MDrawerContent>
          </MDrawerPositioner>
        </Portal>
      </MDrawer>
    </MBox>
  );
};
